import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Loader2, Pause, Play, Plus, RefreshCw, Sparkles, Trash2, Upload, User2 } from "lucide-react";
import type { CuratedVoice } from "@/services/ads/videos/videoDraft.types";
import { useCuratedVoices } from "../hooks/useCuratedVoices";
import { useCustomVoices } from "../hooks/useCustomVoices";
import { dictionary, type Language } from "@/utils/dictionary";
import { useAuth } from "@/context/AuthContext";
import { CustomVoiceCreator } from "./CustomVoiceCreator";

// Phase 2 V2 — Voice picker for modeling voice-over. Renders the eight
// curated voices as a 2-col grid of cards; each card plays its 3s sample
// inline via a single shared <audio> tag (so two samples can't overlap).
//
// Below the grid we expose an ElevenLabs-style settings panel (stability +
// speed) that applies to the script audition. The settings are lifted to
// the parent so generateAudio() can forward them to the backend. Swapping
// the voice keeps the settings — the user tunes once and auditions across.

export interface VoiceSettings {
  stability: number;
  similarity: number;
  style: number;
  speed: number;
}

export const DEFAULT_VOICE_SETTINGS: VoiceSettings = {
  stability: 0.45,
  similarity: 0.75,
  style: 0.55,
  speed: 1.1,
};

interface VoiceLibraryProps {
  selectedVoiceId: string | null;
  onSelectVoice: (voice: CuratedVoice) => void;
  settings: VoiceSettings;
  onSettingsChange: (settings: VoiceSettings) => void;
  /** Disable interaction while an audio generation call is in flight. */
  disabled?: boolean;
  /** Loading skeleton height hint; falls back to auto. */
  minHeight?: number | string;
  /**
   * Phase 2 V4b (Apr 20 2026) — optional "Subir mi voz" callback. When
   * provided, a tile is rendered at the end of the grid that lets the
   * user pick an audio file. The parent is responsible for:
   *   1. Uploading the file to S3
   *   2. Calling setVideoDraftAudioMode(ref, { audio_mode: "own",
   *      uploaded_audio_url: s3Url })
   *   3. Re-fetching the draft so the preview swaps to OwnVoicePanel
   *
   * If omitted, the tile is NOT rendered (keeps the sidebar-time usage
   * unchanged — sidebar controls audio_mode=own via its own 3-pill
   * selector, not through this library).
   */
  onUploadOwnVoice?: (file: File) => Promise<void>;
}

export function VoiceLibrary({
  selectedVoiceId,
  onSelectVoice,
  disabled = false,
  minHeight,
  onUploadOwnVoice,
}: VoiceLibraryProps) {
  const { language } = useAuth();
  const t = (key: string, fallback?: string) =>
    dictionary[key]?.[language as Language] ?? fallback ?? key;
  const { voices, isLoading, error, refresh } = useCuratedVoices();
  // Phase 2 V7 — user custom voices live next to curated ones in the same grid.
  // V8 (Apr 19 evening) — also expose create so the modal doesn't spin up a
  // separate hook instance (which would have its own list state and miss the
  // optimistic insert here). Single source of truth for custom voices lives
  // in this component's hook instance.
  const {
    voices: customVoices,
    remove: removeCustomVoice,
    create: createCustomVoice,
  } = useCustomVoices(true);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Phase 2 V4b — "Subir mi voz" tile state. When the user picks a file,
  // we delegate the upload + draft-mode-swap to the parent via
  // onUploadOwnVoice. The local `uploading` state only drives the tile's
  // visual (spinner + dimmed), so the preview can keep interaction with
  // the rest of the UI. Errors surface via the parent's toast system.
  const ownUploadInputRef = useRef<HTMLInputElement | null>(null);
  const [ownUploading, setOwnUploading] = useState(false);

  const handleOwnUploadClick = () => {
    if (!onUploadOwnVoice || disabled || ownUploading) return;
    ownUploadInputRef.current?.click();
  };

  const handleOwnUploadChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow picking the same file twice in a row
    if (!file || !onUploadOwnVoice) return;
    setOwnUploading(true);
    try {
      await onUploadOwnVoice(file);
      // Success: the parent will swap the preview to OwnVoicePanel via
      // draft re-fetch, unmounting VoiceLibrary. Nothing to do here.
    } catch {
      // Parent surfaces the toast; we just clear the local spinner.
    } finally {
      setOwnUploading(false);
    }
  };

  // Single shared audio element — stopping any previous sample before
  // starting a new one keeps the picker from turning into a cacophony if
  // the user hammer-clicks through the grid.
  useEffect(() => {
    const el = new Audio();
    el.addEventListener("ended", () => setPlayingVoiceId(null));
    el.addEventListener("pause", () => setPlayingVoiceId(null));
    audioRef.current = el;
    return () => {
      el.pause();
      audioRef.current = null;
    };
  }, []);

  const playSample = (voice: CuratedVoice) => {
    const el = audioRef.current;
    const sampleUrl = voice.sample_url;
    if (!el || !sampleUrl) return;
    if (playingVoiceId === voice.voice_id) {
      el.pause();
      setPlayingVoiceId(null);
      return;
    }
    el.pause();
    el.src = sampleUrl;
    el.currentTime = 0;
    el.play().catch(() => {
      // autoplay or network error — silent fail, the user can retry
      setPlayingVoiceId(null);
    });
    setPlayingVoiceId(voice.voice_id);
  };

  // Phase 2 V7 — fold custom voices into the curated grid. `provider: "gemini"`
  // + `tone: "custom"` distinguish them visually (sparkles icon + delete hover).
  const customAsVoices: CuratedVoice[] = useMemo(
    () =>
      customVoices.map((c) => ({
        voice_id: c.voice_id,
        display_name: c.display_name,
        gender: "custom",
        age_range: "Tu voz",
        tone: c.description.slice(0, 48),
        description: c.description,
        sample_url: c.sample_url ?? "",
        language_tags: ["es"],
        recommended_for: [],
        provider: "gemini" as const,
        style_prompt: c.style_prompt,
      })),
    [customVoices],
  );

  const handleDeleteCustom = (refId: string) => {
    const confirmed = window.confirm(
      t("custom-voice-confirm-delete", "¿Borrar esta voz? Los videos que ya la usan se mantienen intactos."),
    );
    if (!confirmed) return;
    const numId = refId.startsWith("custom-") ? refId.slice(7) : refId;
    removeCustomVoice(numId);
  };

  const skeletons = useMemo(() => Array.from({ length: 6 }, (_, i) => i), []);

  if (error && !isLoading) {
    return (
      <div
        className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 flex items-center justify-between gap-3"
        style={minHeight ? { minHeight } : undefined}
      >
        <p className="text-sm text-foreground/80 leading-relaxed">
          {t("video-voice-library-error", "No pudimos cargar las voces. Intenta de nuevo.")}
        </p>
        <button
          type="button"
          onClick={refresh}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          {t("video-voice-library-retry", "Reintentar")}
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-2" style={minHeight ? { minHeight } : undefined}>
        {skeletons.map((i) => (
          <div
            key={i}
            className="rounded-xl border border-border/40 bg-muted/30 animate-pulse h-[76px]"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Voice grid. Each card is a <div role="radio"> (not a <button>) so
          the inline "play sample" <button> is a valid DOM descendant — the
          old <button>-inside-<button> nesting was throwing a React warning
          and breaking accessibility semantics. Keyboard support kept via
          role + onKeyDown for Enter/Space. */}
      <div
        role="radiogroup"
        aria-label={t("video-voice-library-aria", "Elige la voz del video")}
        className="grid grid-cols-2 gap-2"
      >
        {[...voices, ...customAsVoices].map((voice) => {
          const selected = voice.voice_id === selectedVoiceId;
          const playing = voice.voice_id === playingVoiceId;
          const isCustom = voice.gender === "custom";
          const handleSelect = () => {
            if (!disabled) onSelectVoice(voice);
          };
          return (
            <div
              key={voice.voice_id}
              role="radio"
              aria-checked={selected}
              aria-disabled={disabled}
              tabIndex={disabled ? -1 : 0}
              onClick={handleSelect}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSelect();
                }
              }}
              className={[
                "group relative rounded-xl border text-left p-2.5 transition-all",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                selected
                  ? "border-primary/60 bg-gradient-to-br from-primary/5 to-accent/5 shadow-sm"
                  : "border-border/50 bg-white hover:border-primary/40 hover:shadow-sm",
                disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
              ].join(" ")}
            >
              {selected && (
                <div className="absolute top-2 right-2 h-4 w-4 rounded-full bg-primary text-white flex items-center justify-center">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </div>
              )}
              {isCustom && !selected && (
                <button
                  type="button"
                  aria-label={t("custom-voice-delete-aria", "Borrar voz custom")}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteCustom(voice.voice_id);
                  }}
                  className="absolute top-2 right-2 h-5 w-5 rounded-full bg-white/70 text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-600 flex items-center justify-center transition-opacity"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
              <div className="flex items-center gap-2.5">
                <div
                  className={[
                    "h-9 w-9 rounded-full flex items-center justify-center shrink-0",
                    isCustom
                      ? "bg-gradient-to-br from-amber-100 to-primary/10 text-primary"
                      : voice.gender === "female"
                      ? "bg-pink-100 text-pink-600"
                      : "bg-blue-100 text-blue-600",
                  ].join(" ")}
                  aria-hidden="true"
                >
                  {isCustom ? <Sparkles className="h-4 w-4" /> : <User2 className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground leading-tight truncate">
                    {voice.display_name}
                  </p>
                  <p className="text-[11px] text-muted-foreground leading-tight truncate">
                    {voice.age_range} · {voice.tone}
                  </p>
                </div>
                {voice.sample_url && (
                  <button
                    type="button"
                    aria-label={t("video-voice-preview-aria", "Escuchar muestra de voz")}
                    onClick={(e) => {
                      e.stopPropagation();
                      playSample(voice);
                    }}
                    disabled={disabled}
                    className={[
                      "h-7 w-7 rounded-full flex items-center justify-center shrink-0 transition-colors",
                      playing
                        ? "bg-primary text-white"
                        : "bg-muted/60 text-foreground/70 hover:bg-primary/10 hover:text-primary",
                    ].join(" ")}
                  >
                    {playing ? (
                      <Pause className="h-3.5 w-3.5" strokeWidth={2.5} />
                    ) : (
                      <Play className="h-3.5 w-3.5 ml-px" strokeWidth={2.5} />
                    )}
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {/* Phase 2 V7 — "+ Crea tu voz" tile. Always at the end of the grid. */}
        <button
          type="button"
          onClick={() => !disabled && setCreatorOpen(true)}
          disabled={disabled}
          className="rounded-xl border-2 border-dashed border-primary/30 bg-primary/[0.02] p-2.5 hover:border-primary/60 hover:bg-primary/[0.05] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2.5 cursor-pointer"
        >
          <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Plus className="h-4 w-4" strokeWidth={2.5} />
          </div>
          <div className="min-w-0 flex-1 text-left">
            <p className="text-sm font-semibold text-primary leading-tight">
              {t("custom-voice-grid-cta-title", "Crea tu voz")}
            </p>
            <p className="text-[11px] text-muted-foreground leading-tight truncate">
              {t("custom-voice-grid-cta-sub", "Descríbela y te la generamos")}
            </p>
          </div>
        </button>
        {/* Phase 2 V4b (Apr 20 2026) — "Sube tu voz" tile. Only renders
            when the parent passes onUploadOwnVoice. Clicking it opens
            the native file picker; the parent handles the S3 upload +
            set-audio-mode endpoint + draft re-fetch. User feedback:
            "en elige voz no veo la opción de subir mi voz". */}
        {onUploadOwnVoice && (
          <>
            <button
              type="button"
              onClick={handleOwnUploadClick}
              disabled={disabled || ownUploading}
              className="rounded-xl border-2 border-dashed border-indigo-300/50 bg-indigo-50/40 p-2.5 hover:border-indigo-400 hover:bg-indigo-50/70 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2.5 cursor-pointer"
            >
              <div className="h-9 w-9 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
                {ownUploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.5} />
                ) : (
                  <Upload className="h-4 w-4" strokeWidth={2.5} />
                )}
              </div>
              <div className="min-w-0 flex-1 text-left">
                <p className="text-sm font-semibold text-indigo-700 leading-tight">
                  {ownUploading
                    ? t("voice-upload-cta-uploading", "Subiendo...")
                    : t("voice-upload-cta-title", "Sube tu voz")}
                </p>
                <p className="text-[11px] text-muted-foreground leading-tight truncate">
                  {t("voice-upload-cta-sub", "MP3 o WAV con tu audio")}
                </p>
              </div>
            </button>
            <input
              ref={ownUploadInputRef}
              type="file"
              accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/mp4,.mp3,.wav,.m4a"
              className="hidden"
              onChange={handleOwnUploadChange}
            />
          </>
        )}
      </div>

      <CustomVoiceCreator
        open={creatorOpen}
        onClose={() => setCreatorOpen(false)}
        createVoice={createCustomVoice}
        onVoiceCreated={(v) => {
          // Auto-select the freshly created voice — its voice_id lives in
          // the useCustomVoices list after create(). Map to a CuratedVoice
          // shape so the picker's onSelectVoice contract holds.
          onSelectVoice({
            voice_id: v.voice_id,
            display_name: v.display_name,
            gender: "custom",
            age_range: "Tu voz",
            tone: v.description.slice(0, 48),
            description: v.description,
            sample_url: v.sample_url ?? "",
            language_tags: ["es"],
            recommended_for: [],
            provider: "gemini",
            style_prompt: v.style_prompt,
          });
        }}
      />

      {/* Phase 2 V9 REV 2 (Apr 19 2026) — "Opciones avanzadas" HIDDEN.
          Gemini 3.1 Flash TTS (the default provider for all 8 Fluxi
          voices) does NOT accept a `speakingRate` param — pace is
          controlled through the natural-language `paceHint` that the
          backend prepends to the prompt (see VideoDraftService.
          paceHintForDuration). So the speed slider was dead UI: it
          mutated voice_config but had zero effect on the rendered audio.
          With pace hints + the per-duration word budgets in the director
          + the truncate+fade overflow protection, the audio always
          fits the clip without user intervention. Slider removed to
          avoid the "I dragged it and nothing changed" confusion.
          The VoiceSettings object + onSettingsChange prop are kept so
          the legacy Eleven/Cloud paths (if ever re-enabled) still work. */}
    </div>
  );
}
